import { ReactElement, useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import { Vector3 } from 'three'
import { CheckSettingsFormValues } from '../../MultiEditCheckSettings/types'
import useSupportMarkerPosition from '../hooks/useSupportMarkerPosition'
import useToggleExcludedSupports from '../hooks/useToggleExcludedSupports'
import { SharedSupportCompressionSettings, getSupportCellStates, getSupportConfigs } from '../utils'
import SupportSceneCheckboxEffect from './SupportSceneCheckboxEffect'

interface SceneSupportCheckbox {
  elementGuid: string
  supportIndex: number
  position: Vector3
  isChecked: boolean
}

/**
 * Puts a checkbox on every support the Auflagerpressung multi-edit can cover, at
 * its position in the model — checked while the support takes part in the edit,
 * unchecked once it was removed. Supports that are no real supports get none:
 * they are not part of the edit at all. Must live inside the section's form.
 */
const SupportSceneCheckboxesEffect = (): ReactElement => {
  const resolveSupportPosition = useSupportMarkerPosition()
  const { toggleSupport } = useToggleExcludedSupports()

  const elements = useWatch<CheckSettingsFormValues<SharedSupportCompressionSettings>>({
    name: 'elements',
  }) as CheckSettingsFormValues['elements']
  const sharedSettings = useWatch<CheckSettingsFormValues<SharedSupportCompressionSettings>>({
    name: 'sharedSettings',
  }) as SharedSupportCompressionSettings | undefined

  const supports = useMemo<SceneSupportCheckbox[]>(
    () =>
      elements.flatMap(element => {
        const supportConfigs = getSupportConfigs(element.checkSettings) ?? []
        const states = getSupportCellStates(element, sharedSettings, supportConfigs.length)

        return supportConfigs.flatMap((config, supportIndex) => {
          if (states[supportIndex] === 'not-applicable') return []

          const position = resolveSupportPosition(element.guid, config.relative_position)
          if (!position) return []

          return [
            {
              elementGuid: element.guid,
              supportIndex,
              position,
              isChecked: states[supportIndex] === 'editable',
            },
          ]
        })
      }),
    [elements, sharedSettings, resolveSupportPosition],
  )

  return (
    <>
      {supports.map(({ elementGuid, supportIndex, position, isChecked }) => (
        <SupportSceneCheckboxEffect
          key={`${elementGuid}-${supportIndex}`}
          elementGuid={elementGuid}
          supportIndex={supportIndex}
          position={position}
          isChecked={isChecked}
          onToggle={toggleSupport}
        />
      ))}
    </>
  )
}

export default SupportSceneCheckboxesEffect
